import { LocalizationString } from '@celito.clients/assets';
import { ActionTypeEnum, ButtonTypes, DateFormat } from '@celito.clients/enums';
import { useTheme } from '@celito.clients/theme';
import { DateType } from '@celito.clients/types';
import { formatDate } from '@celito.clients/utils';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Label,
  mergeClasses,
} from '@fluentui/react-components';
import {
  CustomButton,
  DatePicker,
  Icon,
  PeoplePicker,
  TextFieldArea,
} from 'libs/shared/src/lib/shared';
import React from 'react';
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form';

import {
  ExtensionRequestConfig,
  ExtensionRequestDialogProps,
} from './extension-request-dialog.model';
import classes from './extension-request-dialog.module.css';
import { extensionRequestDialogStyles } from './extension-request-dialog-styles';

interface ExtensionRequestDialogViewProps<T extends FieldValues = FieldValues>
  extends ExtensionRequestDialogProps {
  control: Control<T, object>;
  onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onPopupClose: () => void;
  currentDueDate: Date | null;
  extensionRequestConfig: ExtensionRequestConfig | null;
  errors: FieldErrors<T>;
  handleApproverRequestAccept?: (
    event: React.SyntheticEvent,
    items?: unknown[]
  ) => void;
}

export function ExtensionRequestDialogView<
  T extends FieldValues = FieldValues,
>({
  isOpen,
  onFormSubmit,
  rowItem,
  control,
  isLoading,
  onPopupClose,
  actionType,
  currentDueDate,
  extensionRequestConfig,
  errors,
  handleApproverRequestAccept,
}: Readonly<ExtensionRequestDialogViewProps<T>>): JSX.Element {
  const { cssVariables } = useTheme();
  const styles = extensionRequestDialogStyles();

  const getDialogTitle = () => {
    switch (actionType) {
      case ActionTypeEnum.REQUEST_EXTENSION:
        return LocalizationString.REQUEST_EXTENSION;

      case ActionTypeEnum.CANCEL_EXTENSION:
        return LocalizationString.CANCEL_EXTENSION;
      default:
        return '';
    }
  };

  const getSubmitButtonText = () => {
    switch (actionType) {
      case ActionTypeEnum.REQUEST_EXTENSION:
        return LocalizationString.REQUEST;
      case ActionTypeEnum.CANCEL_EXTENSION:
        return LocalizationString.YES;
      default:
        return LocalizationString.SUBMIT;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(e) => {
        e.stopPropagation();
        onPopupClose();
      }}
    >
      <DialogSurface
        className={styles.container}
        style={{ padding: 0, ...cssVariables }}
      >
        <DialogBody onClick={(e) => e.stopPropagation()}>
          <DialogTitle className={styles.modalHeader}>
            {getDialogTitle()}
            <Button
              appearance="subtle"
              icon={<Icon iconName="Dismiss24Regular" />}
              onClick={onPopupClose}
            />
          </DialogTitle>

          <form
            onSubmit={onFormSubmit}
            noValidate
            className={styles.modalWidth}
          >
            <DialogContent className={styles.modalBody}>
              {currentDueDate && (
                <div
                  className={mergeClasses(
                    styles.currentDueDateInfo,
                    classes.infoBox
                  )}
                >
                  <Label className={classes.label}>
                    {LocalizationString.ORIGINAL_DUE_DATE}:{' '}
                  </Label>
                  <Label className={classes.value}>
                    {formatDate(currentDueDate as DateType, DateFormat.Date)}
                  </Label>
                </div>
              )}

              {/* Fields for REQUEST_EXTENSION */}
              {actionType === ActionTypeEnum.REQUEST_EXTENSION && (
                <>
                  <div
                    className={mergeClasses(
                      styles.fieldsWidth,
                      classes.extensionFields
                    )}
                  >
                    <Controller
                      name={'requestedDueDate' as Path<T>}
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          id="requestedDueDate"
                          label={LocalizationString.REQUESTED_DUE_DATE}
                          onDateChange={field.onChange}
                          value={field.value}
                          placeholder={LocalizationString.SELECT_DATE}
                          minDate={currentDueDate || undefined}
                          className={styles.date}
                          required
                          error={
                            errors.requestedDueDate as FieldError | undefined
                          }
                        />
                      )}
                    />
                  </div>

                  <div
                    className={mergeClasses(
                      styles.fieldsWidth,
                      classes.extensionFields
                    )}
                  >
                    <Controller
                      name={'justification' as Path<T>}
                      control={control}
                      render={({ field }) => (
                        <TextFieldArea
                          id="justification"
                          label={LocalizationString.REASON_FOR_REQUEST}
                          value={field.value || ''}
                          onPaste={(e) => {
                            const input =
                              e.currentTarget as HTMLTextAreaElement;
                            setTimeout(() => field.onChange(input.value), 0);
                          }}
                          onChange={(_, data) => field.onChange(data.value)}
                          placeholder={LocalizationString.ENTER_REASON}
                          required
                          errorMessage={
                            errors.justification?.message &&
                            typeof errors.justification.message === 'string'
                              ? errors.justification.message
                              : ''
                          }
                        />
                      )}
                    />
                  </div>

                  {extensionRequestConfig?.approverRoles &&
                    extensionRequestConfig.approverRoles.length > 0 && (
                      <div
                        className={mergeClasses(
                          styles.fieldsWidth,
                          classes.extensionFields
                        )}
                      >
                        <Controller
                          name={'approvers' as Path<T>}
                          control={control}
                          render={({ field }) => (
                            <PeoplePicker
                              id="approvers"
                              label={LocalizationString.QUALITY_APPROVERS}
                              selectedOptions={field.value || []}
                              multiselect={true}
                              onOptionSelect={(ev, data) => {
                                // Update the form field with selected options
                                field.onChange(data.selectedOptions);
                                // Handle approver request accept if provided
                                if (handleApproverRequestAccept && data) {
                                  handleApproverRequestAccept(
                                    ev,
                                    Array.isArray(data) ? data : [data]
                                  );
                                }
                              }}
                              required
                              roles={extensionRequestConfig.approverRoles.map(
                                (role) => ({
                                  name: role,
                                })
                              )}
                              excludeUsers={[
                                rowItem.owner || rowItem.requestor,
                              ]}
                              filterByExactRoles={false}
                              errorMessage={
                                errors.approvers?.message &&
                                typeof errors.approvers.message === 'string'
                                  ? errors.approvers.message
                                  : ''
                              }
                            />
                          )}
                        />
                      </div>
                    )}
                </>
              )}

              {/* Fields for CANCEL_EXTENSION */}
              {actionType === ActionTypeEnum.CANCEL_EXTENSION && (
                <>
                  <div className={styles.confirmationSection}>
                    <div className={styles.iconContainer}>
                      <Icon
                        iconName="DismissCircle24Regular"
                        className={styles.deleteIcon}
                      />
                    </div>
                    <div className={styles.confirmationMessage}>
                      Are you sure you want to cancel the extension request?
                    </div>
                  </div>
                  <div
                    className={mergeClasses(
                      styles.fieldsWidth,
                      classes.extensionFields
                    )}
                  >
                    <Controller
                      name={'reasonForCancelation' as Path<T>}
                      control={control}
                      render={({ field }) => (
                        <TextFieldArea
                          id="reasonForCancelation"
                          label={LocalizationString.REASON_FOR_CANCELATION}
                          value={field.value || ''}
                          onPaste={(e) => {
                            const input =
                              e.currentTarget as HTMLTextAreaElement;
                            setTimeout(() => field.onChange(input.value), 0);
                          }}
                          onChange={(_, data) => field.onChange(data.value)}
                          placeholder="Enter cancelation reason"
                          required
                          errorMessage={
                            errors.reasonForCancelation?.message &&
                            typeof errors.reasonForCancelation.message ===
                              'string'
                              ? errors.reasonForCancelation.message
                              : ''
                          }
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </DialogContent>

            <DialogActions className={styles.modalFooter}>
              <CustomButton
                buttonType={ButtonTypes.Ghost}
                buttonTitle={LocalizationString.CANCEL}
                onClick={onPopupClose}
              />
              <CustomButton
                buttonType={ButtonTypes.Primary}
                buttonTitle={getSubmitButtonText()}
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
                iconPosition="before"
              />
            </DialogActions>
          </form>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
